import React from "react";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  sortableKeyboardCoordinates,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CirclePlus, FileTextIcon, GripVertical, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface SortableServiceItem {
  id: string;
  serviceId: string;
  name: string;
}

interface SortableServiceListProps {
  items: SortableServiceItem[];
  disabled?: boolean;
  onReorder: (items: SortableServiceItem[]) => void;
  onRemove: (id: string) => void;
  onAdd: (serviceId: string) => void;
  availableServices: { id: string; name: string }[];
  placeholder?: string;
}

interface SortableItemProps {
  item: SortableServiceItem;
  onRemove: (id: string) => void;
}

const SortableItem = ({ item, onRemove }: SortableItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 border-b border-gray-200 bg-white py-2"
    >
      <div className="flex flex-1 items-center gap-2">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab text-gray-400 hover:text-gray-600"
        >
          <GripVertical className="size-4" />
        </div>
        <div className="flex size-5 items-center justify-center text-gray-400">
          <FileTextIcon className="size-4" />
        </div>
        <span className="text-sm">{item.name}</span>
      </div>
      <Button
        type="button"
        variant="new_ghost"
        size="icon_sm"
        className="text-gray-400 hover:bg-transparent hover:text-red-500"
        onClick={() => onRemove(item.id)}
        aria-label={`Remove ${item.name}`}
      >
        <Trash2 />
      </Button>
    </div>
  );
};

export const SortableServiceList = ({
  items,
  disabled = false,
  onReorder,
  onRemove,
  onAdd,
  availableServices,
  placeholder = "Add service...",
}: SortableServiceListProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const [isAdding, setIsAdding] = React.useState(false);

  const filteredAvailable = availableServices.filter(
    (service) => !items.some((item) => item.serviceId === service.id),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      onReorder(arrayMove(items, oldIndex, newIndex));
    }
  };

  const handleAdd = (serviceId: string) => {
    onAdd(serviceId);
    setIsAdding(false);
  };

  return (
    <div className="w-full">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          <div className="space-y-1">
            {items.map((item) => (
              <SortableItem key={item.id} item={item} onRemove={onRemove} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <div className="mt-2 flex items-center gap-3 py-2">
        <div className="flex flex-1 items-center gap-2 border-b border-gray-200 pb-2">
          <Button
            type="button"
            variant="new_ghost"
            size="icon_sm"
            disabled={disabled}
            onClick={() => setIsAdding((current) => !current)}
            aria-label="Add service"
          >
            <CirclePlus />
          </Button>
          {isAdding ? (
            <Select onValueChange={handleAdd}>
              <SelectTrigger className="w-full justify-between">
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {filteredAvailable.length === 0 ? (
                    <SelectItem value="none" disabled>
                      No services available
                    </SelectItem>
                  ) : (
                    filteredAvailable.map((service) => (
                      <SelectItem key={service.id} value={service.id}>
                        {service.name}
                      </SelectItem>
                    ))
                  )}
                </SelectGroup>
              </SelectContent>
            </Select>
          ) : null}
        </div>
      </div>
    </div>
  );
};
